import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useDeferApi } from "@core-ui/hooks/useApi";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import getCatalogNameWithoutCommitOid from "@ext/git/actions/Revisions/logic/utils/getCatalogNameWithoutCommitOid";
import { parsePathnameScope } from "@ext/git/actions/Revisions/logic/utils/parsePathnameScope";
import { useDiffMode } from "@ext/git/core/Diff/logic/hooks/useDiffMode";
import { disableLiveDiff, enableLiveDiff, useLiveDiffAttached } from "@ext/git/core/Diff/logic/live/liveDiffStore";
import { GitTreeScopeParser } from "@ext/versioning/GitTreeScopeParser";
import { useCallback, useRef } from "react";

const toScope = (commitOid?: string) => (commitOid ? GitTreeScopeParser.toString({ commit: commitOid }) : undefined);

/**
 * Opens a revision inside the current article; the catalog tree stays current. Only a catalog that is already
 * open on a commit (`<catalog>:commit-<sha>` address) moves to the clicked revision as a whole.
 */
export const useGotoRevision = () => {
	const router = useRouter();
	const attached = useLiveDiffAttached();
	const { isMarkdown } = useDiffMode();
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const articlePath = useArticlePropsStore((state) => state.data?.ref?.path);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const live = attached && !!catalogName && !!articlePath;
	const isCatalogOnCommit = !!catalogName && getCatalogNameWithoutCommitOid(catalogName) !== catalogName;
	const requested = useRef<{ commitOid: string; oldCommitOid?: string } | null>(null);

	const { call: gotoRevision } = useDeferApi<string, string>({
		onDone: (pathname) => {
			const { commitOid, oldCommitOid } = requested.current ?? {};
			if (isCatalogOnCommit && !oldCommitOid) {
				void router.pushPath(pathname, { diff: undefined, oldScope: undefined });
				return;
			}
			const hasCommitOid = pathname.includes("commit-") || pathname.includes("dif-");
			if (!hasCommitOid) {
				if (live) disableLiveDiff(router);
				else void router.pushPath(pathname, { diff: undefined, oldScope: undefined });
				return;
			}
			if (live && commitOid) {
				void enableLiveDiff({
					catalogName,
					articlePath,
					scope: toScope(commitOid),
					oldScope: toScope(oldCommitOid),
					view: isMarkdown ? "markdown" : "wysiwyg",
					apiUrlCreator,
					router,
				});
				return;
			}
			// No live editor here (a deleted article's page): the revision opens by address, in the current catalog.
			void router.pushPath(parsePathnameScope(pathname).cleanPath, {
				diff: "1",
				scope: toScope(commitOid),
				oldScope: toScope(oldCommitOid),
			});
		},
	});

	return useCallback(
		(commitOid: string, oldCommitOid?: string) => {
			requested.current = { commitOid, oldCommitOid };
			return gotoRevision({ url: (api) => api.getPathnameToRevision(commitOid, oldCommitOid) });
		},
		[gotoRevision],
	);
};
